/**
 * "<Name> is calling · Video call in #channel" with Join / Decline (TRUS-668).
 * Rings (WebAudio) and raises one desktop notification per call while shown —
 * also while the window is in the background (the ringing list keeps polling
 * there). Clicking the notification focuses the window and opens the join
 * flow (consent first for a recorded call); it is cleared on join / decline /
 * when the call stops ringing. See callNotifications.ts.
 * v3: an invite reads "<Name> invited you to a call" (the inviter when the
 * server names them, else whoever started it); an invitee who cannot read
 * the channel joins into the panel alone.
 */

import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Phone, PhoneOff, Video as VideoIcon } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { Call } from '@/services/callsApi';
import type { Channel } from '@/services/messagingApi';
import { Button } from '../../ui/button';
import { callStore } from './callStore';
import { useCallsApi } from './useCalls';
import { startRinging, stopRinging } from './ringtone';
import {
  __resetCallNotifications,
  clearCallNotification,
  maybeAskCallNotificationPermission,
  showCallNotification,
} from './callNotifications';
import { listenForPushShown } from '@/lib/browserNotifications';

/** Calls already notified (never twice), and those whose notification is still up. */
const notifiedCallIds = new Set<number>();
const shownCallIds = new Set<number>();
const callTag = (id: number) => `call-${id}`;

/** Where a call notification's click goes (the push uses the same links): the join flow. */
function callClickUrl(id: number): string {
  const member = typeof window !== 'undefined' && window.location.pathname.startsWith('/member');
  return member ? `/member/inbox?call=${id}` : `/workspace-messaging?call=${id}`;
}

/** Tests only. */
export function resetCallNotifications() {
  notifiedCallIds.clear();
  shownCallIds.clear();
  __resetCallNotifications();
}

/** A channel's display name from the cached channel list, if we have it. */
function useChannelName(): (channelId: number) => string | null {
  const queryClient = useQueryClient();
  return (channelId: number) => {
    const list = queryClient.getQueryData<Channel[]>(['messaging-channels']);
    const ch = Array.isArray(list) ? list.find((c) => c.id === channelId) : undefined;
    return ch ? ch.display_name || ch.name || null : null;
  };
}

interface IncomingCallBannerProps {
  calls: Call[];
  /** Ring + desktop notification (main window only). */
  ring: boolean;
  /** Opened the call: let the messaging workspace show its channel. */
  onJoined?: (call: Call) => void;
}

export function IncomingCallBanner({ calls, ring, onJoined }: IncomingCallBannerProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const queryClient = useQueryClient();
  const channelName = useChannelName();

  const describe = (call: Call) => {
    const modeLabel =
      call.mode === 'video' ? t('messaging.calls.videoCall', 'Video call') : t('messaging.calls.audioCall', 'Audio call');
    const name = call.can_open_channel === false ? null : channelName(call.channel_id);
    const caller = call.started_by?.name || t('messaging.calls.someone', 'Someone');
    if (call.invited) {
      const inviter = call.invited_by?.name || caller;
      return {
        title: t('messaging.calls.invitedYou', '{{name}} invited you to a call', { name: inviter }),
        subtitle: call.recording
          ? t('messaging.calls.modeRecorded', '{{mode}} · Recording', { mode: modeLabel })
          : modeLabel,
        channel: name,
      };
    }
    const where =
      // Every room is 'group' now (Twilio retired 'go'); the server says if it's a DM.
      call.is_dm || call.room_type === 'go'
        ? t('messaging.calls.directMessage', 'direct message')
        : name
          ? `#${name}`
          : t('messaging.calls.aChannel', 'a channel');
    return {
      title: t('messaging.calls.isCalling', '{{name}} is calling', { name: caller }),
      subtitle: call.recording
        ? t('messaging.calls.modeInRecorded', '{{mode}} in {{where}} · Recording', { mode: modeLabel, where })
        : t('messaging.calls.modeIn', '{{mode}} in {{where}}', { mode: modeLabel, where }),
      channel: name,
    };
  };

  /** "Video call · #channel" / "Audio call · direct message" for the notification. */
  const notificationBody = (call: Call) => {
    const modeLabel =
      call.mode === 'video' ? t('messaging.calls.videoCall', 'Video call') : t('messaging.calls.audioCall', 'Audio call');
    const name = call.can_open_channel === false ? null : channelName(call.channel_id);
    const where =
      call.is_dm || call.room_type === 'go'
        ? t('messaging.calls.directMessage', 'direct message')
        : name
          ? `#${name}`
          : null;
    return where ? t('messaging.calls.notifyBody', '{{mode}} · {{where}}', { mode: modeLabel, where }) : modeLabel;
  };

  const joinRef = useRef<(call: Call) => void>(() => {});

  const ids = calls.map((c) => c.id).join(',');
  useEffect(() => {
    // A call that stopped ringing (answered elsewhere, expired, ended): clear its notification.
    const ringing = new Set(calls.map((c) => c.id));
    for (const id of Array.from(shownCallIds)) {
      if (!ringing.has(id)) {
        shownCallIds.delete(id);
        clearCallNotification(callTag(id));
      }
    }
    if (!ring || calls.length === 0) {
      stopRinging();
      return;
    }
    startRinging();
    for (const call of calls) {
      if (notifiedCallIds.has(call.id)) continue;
      notifiedCallIds.add(call.id);
      shownCallIds.add(call.id);
      const d = describe(call);
      void showCallNotification({
        title: d.title,
        body: notificationBody(call),
        tag: callTag(call.id),
        requireInteraction: true,
        data: {
          type: 'call',
          call_id: call.id,
          channel_id: call.channel_id,
          // The "call started" entry's in-app notification names this message:
          // while the call rings, the bell does not raise a second one for it.
          ...(call.message_id ? { message_id: call.message_id } : {}),
        },
        // Browser: the call's push has this same tag; the two collapse.
        collapseWithPush: { url: callClickUrl(call.id) },
        onClick: () => joinRef.current(call),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, ring]);

  useEffect(() => () => stopRinging(), []);
  // Remember the calls a push already showed in this browser (no second one).
  useEffect(() => listenForPushShown(), []);

  if (calls.length === 0) return null;

  const joinCall = (call: Call) => {
    stopRinging();
    shownCallIds.delete(call.id);
    clearCallNotification(callTag(call.id));
    // The first call someone answers is when we ask to notify them of the next one.
    maybeAskCallNotificationPermission();
    callStore.open({
      callId: call.id,
      channelId: call.channel_id,
      mode: call.mode,
      channelName: describe(call).channel || undefined,
      // v2: a recorded call asks for consent before /join.
      consentNeeded: !!call.recording && !call.my_recording_consent,
      // v3: invited without channel access — the panel alone.
      standalone: call.can_open_channel === false,
    });
    onJoined?.(call);
  };
  joinRef.current = joinCall;

  const decline = (call: Call) => {
    callStore.dismiss(call.id);
    shownCallIds.delete(call.id);
    clearCallNotification(callTag(call.id));
    void api
      .decline(call.id)
      .catch(() => {})
      .finally(() => queryClient.invalidateQueries({ queryKey: ['calls-ringing'] }));
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[70] flex flex-col items-center gap-2 px-4" data-testid="incoming-calls">
      {calls.slice(0, 3).map((call) => {
        const d = describe(call);
        return (
          <div
            key={call.id}
            role="alertdialog"
            aria-label={d.title}
            data-testid={`incoming-call-${call.id}`}
            className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-emerald-300 bg-white px-3 py-2 shadow-xl dark:border-emerald-700 dark:bg-slate-900"
          >
            <div className="flex h-9 w-9 flex-shrink-0 animate-pulse items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              {call.mode === 'video' ? <VideoIcon className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{d.title}</div>
              <div className="truncate text-xs text-muted-foreground">{d.subtitle}</div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1 border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => decline(call)}
              data-testid={`decline-call-${call.id}`}
            >
              <PhoneOff className="h-3.5 w-3.5" />
              {t('messaging.calls.decline', 'Decline')}
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={() => joinCall(call)}
              data-testid={`join-call-${call.id}`}
            >
              {call.mode === 'video' ? <VideoIcon className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
              {t('messaging.calls.join', 'Join')}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
